import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { apiService } from '../services/apiService'

const AdminAuthContext = createContext()

const IDLE_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const VISIBILITY_GRACE_MS = 30 * 1000 // 30 seconds grace when tab hidden

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const idleTimerRef = useRef(null)
  const visibilityTimerRef = useRef(null)
  const isAuthenticatedRef = useRef(false)

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Idle timeout & beforeunload setup
  useEffect(() => {
    if (!isAuthenticated) return

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        console.warn('Admin session timed out due to inactivity')
        logout()
      }, IDLE_TIMEOUT_MS)
    }

    const handleActivity = () => resetIdleTimer()
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    activityEvents.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }))
    resetIdleTimer()

    const handleBeforeUnload = () => {
      if (isAuthenticatedRef.current) {
        apiService.beaconLogout(true, false)
        sessionStorage.removeItem('adminToken')
        sessionStorage.removeItem('adminData')
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    const handleVisibility = () => {
      if (document.hidden) {
        visibilityTimerRef.current = setTimeout(() => {
          console.warn('Admin session ended — tab hidden too long')
          logout()
        }, VISIBILITY_GRACE_MS)
      } else {
        if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
        resetIdleTimer()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, handleActivity))
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibility)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
    }
  }, [isAuthenticated])

  const checkAuthStatus = async () => {
    try {
      const token = sessionStorage.getItem('adminToken')
      const adminData = sessionStorage.getItem('adminData')
      
      if (token && adminData) {
        setAdmin(JSON.parse(adminData))
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error('Admin auth check failed:', error)
      logout()
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (mg_code, username, password) => {
    try {
      setIsLoading(true)
      const response = await apiService.adminLogin({
        mgCode: mg_code,
        username,
        password
      })

      if (response.success) {
        sessionStorage.setItem('adminToken', response.token)
        sessionStorage.setItem('adminData', JSON.stringify(response.admin))
        setAdmin(response.admin)
        setIsAuthenticated(true)
        return { success: true }
      } else {
        return { success: false, error: response.message }
      }
    } catch (error) {
      console.error('Admin login failed:', error)
      return { success: false, error: 'Login failed. Please try again.' }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    apiService.serverLogout(true, false).catch(() => {})
    sessionStorage.removeItem('adminToken')
    sessionStorage.removeItem('adminData')
    setAdmin(null)
    setIsAuthenticated(false)
  }

  const value = {
    admin,
    isAuthenticated,
    isLoading,
    login,
    logout
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}
